import { useEffect, useState } from 'react';

import type { ChatSidebarMode } from '@/contexts/ChatSidebarContextDef';
import { cn } from '@/lib/utils';

import { StellaAvatar } from '@/components/stella/StellaAvatar';

const VARIANT_CONFIG = {
  default: {
    avatar: 'h-10 w-10 @[550px]:h-14 @[550px]:w-14', // 56px
    greeting: 'text-lg @[550px]:text-xl', // 20px
    question: 'text-xl @[550px]:text-2xl', // 24px
  },
  large: {
    avatar: 'h-12 w-12 @[550px]:h-16 @[550px]:w-16', // 68px
    greeting: 'text-xl @[550px]:text-2xl', // 24px
    question: 'text-2xl @[550px]:text-4xl', // 36px
  },
} as const;

interface ChatEmptyStateProps {
  userName?: string;
  className?: string;
  variant?: keyof typeof VARIANT_CONFIG;
  mode?: ChatSidebarMode;
}

export const ChatEmptyState = ({
  userName,
  className,
  variant = 'default',
  mode,
}: ChatEmptyStateProps) => {
  const config = VARIANT_CONFIG[variant];
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={cn(
        '@container w-full transition-[opacity,transform] duration-500 ease-out',
        variant === 'large' && 'mb-2',
        mode === 'sidebar' ? 'px-6' : 'px-4',
        {
          'opacity-0 -translate-y-4': !visible,
          'opacity-100 translate-y-0': visible,
        }
      )}
    >
      <div
        className={cn(
          'flex flex-col @[400px]:flex-row @[400px]:items-center gap-4',
          className
        )}
      >
        <StellaAvatar className={config.avatar} />
        <div className="flex flex-col">
          <p className={cn('text-fg', config.greeting)}>
            Hi {userName || 'there'}
          </p>
          <p className={cn('font-bold text-fg', config.question)}>
            What do you want to do?
          </p>
        </div>
      </div>
    </div>
  );
};
